// frontend/src/components/engineer/pages/NoBreakReportPage.tsx - COMPLETE VERSION
import React, { useState, useRef } from 'react';
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { noBreakService, NoBreakRequestData } from '../../../services/noBreak.service';
import * as XLSX from 'xlsx';
import styles from './CreateOvertimePage.module.css';

interface NoBreakReportPageProps {
  reportData: {
    projectName: string;
    projectCode: string;
    selectedDate: Date;
    reason: string;
    employees: any[];
    appliedBy: string;
    appliedAt: Date;
  };
  onBack: () => void;
}

const NoBreakReportPage: React.FC<NoBreakReportPageProps> = ({ reportData, onBack }) => {
  const [isApplying, setIsApplying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [applicationResult, setApplicationResult] = useState<{
    success: boolean;
    message: string;
    details?: any[];
  } | null>(null);
  
  const reportRef = useRef<HTMLDivElement>(null);

  // Apply No-Break Status
  const handleApplyNoBreak = async () => {
    setIsApplying(true);
    setApplicationResult(null);

    try {
      // 🔧 FIXED: Added projectName and projectCode
      const requestData: NoBreakRequestData = {
        projectName: reportData.projectName,
        projectCode: reportData.projectCode,
        selectedDate: reportData.selectedDate,
        employeeIds: reportData.employees.map(emp => emp.id),
        requesterId: 'system', // You can get this from user context
        requesterName: reportData.appliedBy,
        reason: reportData.reason
      };

      const result = await noBreakService.applyNoBreakStatus(requestData);
      setApplicationResult(result);

    } catch (error) {
      console.error('Error applying no-break status:', error);
      setApplicationResult({
        success: false,
        message: 'Failed to apply no-break status. Please try again.'
      });
    } finally {
      setIsApplying(false);
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    setIsExporting(true);

    try {
      // Prepare Excel data
      const excelData = reportData.employees.map((emp, index) => ({
        'S.No': index + 1,
        'Employee Name': emp.name,
        'AUX No': emp.pin || emp.employeeNumber || 'N/A',
        'Designation': emp.designation,
        'Department': emp.department,
        'Check In Time': emp.checkInTime || 'Not recorded',
        'Check Out Time': emp.checkOutTime || 'Not recorded',
        'Attendance Status': emp.hasAttendanceToday ? (emp.attendanceStatus || 'Present') : 'No Attendance',
        'Has Attendance': emp.hasAttendanceToday ? 'Yes' : 'No'
      }));

      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Add company header data
      const headerData = [
        ['PHOENICIAN TECHNICAL SERVICES LLC'],
        ['No Break Report'],
        [''],
        ['Schon Business Park - 399 1 - Dubai Investments Park - Dubai'],
        ['Phone: 04 882 9484'],
        [''],
        ['Project Name:', reportData.projectName],
        ['Project Code:', reportData.projectCode],
        ['Date:', reportData.selectedDate.toLocaleDateString()],
        ['Reason:', reportData.reason],
        ['Applied By:', reportData.appliedBy],
        ['Applied At:', reportData.appliedAt.toLocaleString()],
        ['Total Employees:', reportData.employees.length.toString()],
        ['Employees with Attendance:', reportData.employees.filter(emp => emp.hasAttendanceToday).length.toString()],
        ['Employees without Attendance:', reportData.employees.filter(emp => !emp.hasAttendanceToday).length.toString()],
        ['']
      ];

      // Combine header and data
      const finalData = [...headerData, Object.keys(excelData[0]), ...excelData.map(row => Object.values(row))];
      
      const ws = XLSX.utils.aoa_to_sheet(finalData);
      
      // Style the header - set column widths
      ws['!cols'] = [
        { wch: 5 },  // S.No
        { wch: 25 }, // Employee Name
        { wch: 12 }, // AUX No
        { wch: 20 }, // Designation
        { wch: 18 }, // Department
        { wch: 15 }, // Check In
        { wch: 15 }, // Check Out
        { wch: 15 }, // Attendance Status
        { wch: 12 }  // Has Attendance
      ];
      
      // Merge cells for header
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } }, // Company name
        { s: { r: 1, c: 0 }, e: { r: 1, c: 8 } }, // Report title
        { s: { r: 3, c: 0 }, e: { r: 3, c: 8 } }, // Address
        { s: { r: 4, c: 0 }, e: { r: 4, c: 8 } }  // Phone
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, 'No Break Report');
      
      // Generate filename
      const dateStr = reportData.selectedDate.toISOString().split('T')[0];
      const filename = `NoBreak_Report_${reportData.projectCode}_${dateStr}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, filename);
      
      console.log('Excel report exported successfully');
      
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Print Report
  const handlePrint = () => {
    const printContent = reportRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Create print content with embedded styles
    const printHTML = `
      <html>
        <head>
          <title>No Break Report - ${reportData.projectName}</title>
          <style>
            @page {
              margin: 0.5in;
              size: A4;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              margin: 0;
              padding: 20px;
              color: #333;
              line-height: 1.4;
            }
            
            .print-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #c29e5d;
              padding-bottom: 20px;
            }
            
            .company-logo {
              max-width: 200px;
              height: auto;
              margin-bottom: 10px;
            }
            
            .company-title {
              font-size: 24px;
              font-weight: bold;
              color: #c29e5d;
              margin: 10px 0;
            }
            
            .report-title {
              font-size: 18px;
              font-weight: 600;
              color: #666;
              margin: 5px 0 15px 0;
            }
            
            .company-info {
              font-size: 12px;
              color: #666;
              line-height: 1.6;
            }
            
            .info-section {
              background: #f8f9fa;
              padding: 20px;
              margin: 20px 0;
              border-radius: 8px;
              border-left: 4px solid #c29e5d;
            }
            
            .info-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
              gap: 15px;
              margin-bottom: 15px;
            }
            
            .info-item {
              margin-bottom: 10px;
            }
            
            .info-label {
              font-weight: bold;
              color: #333;
            }
            
            .info-value {
              color: #666;
            }
            
            .report-table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
              font-size: 10px;
            }
            
            .report-table th,
            .report-table td {
              border: 1px solid #ddd;
              padding: 6px 8px;
              text-align: left;
              vertical-align: top;
            }
            
            .report-table th {
              background-color: #f8f9fa;
              font-weight: bold;
              font-size: 10px;
              color: #333;
            }
            
            .report-table tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            
            .status-badge {
              padding: 2px 6px;
              border-radius: 4px;
              font-size: 9px;
              font-weight: 500;
            }
            
            .status-present {
              background: #d1fae5;
              color: #065f46;
            }
            
            .status-absent {
              background: #fee2e2;
              color: #991b1b;
            }
            
            .stats-section {
              background: #fff7ed;
              padding: 20px;
              margin: 20px 0;
              border-radius: 8px;
              border: 1px solid #fed7aa;
            }
            
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              text-align: center;
            }
            
            .stat-item {
              padding: 10px;
            }
            
            .stat-value {
              font-size: 20px;
              font-weight: bold;
              color: #ea580c;
            }
            
            .stat-label {
              font-size: 11px;
              color: #9a3412;
              font-weight: 500;
            }
            
            .footer-section {
              text-align: center;
              margin-top: 30px;
              padding-top: 20px;
              border-top: 2px solid #e5e7eb;
            }
            
            .footer-logo {
              max-width: 300px;
              height: auto;
              opacity: 0.8;
            }
            
            .generated-info {
              margin-top: 15px;
              font-size: 10px;
              color: #6b7280;
              font-style: italic;
            }
            
            /* Print specific styles */
            @media print {
              body { 
                margin: 0;
                font-size: 11px;
              }
              
              .no-print { 
                display: none !important; 
              }
              
              .report-table {
                font-size: 9px;
              }
              
              .report-table th,
              .report-table td {
                padding: 4px 6px;
              }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `;

    printWindow.document.write(printHTML);
    printWindow.document.close();
    
    // Wait for images to load before printing
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  };

  return (
    <div className={styles.createOvertimeContainer}>
      <div className={styles.pageWrapper}>
        {/* Header with Navigation */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '2rem',
          padding: '1rem 2rem',
          background: 'white',
          borderRadius: '1rem',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <button
            onClick={onBack}
            className={styles.navButton}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: '#f3f4f6',
              color: '#374151',
              border: '2px solid #e5e7eb',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.75rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <ArrowLeftIcon style={{ width: '1rem', height: '1rem' }} />
            Back to Selection
          </button>

          <h1 style={{
            margin: 0,
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#322821',
            textAlign: 'center'
          }}>
            No Break Report Preview
          </h1>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleExportExcel}
              disabled={isExporting}
              className={styles.navButton}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'linear-gradient(135deg, #059669, #10b981)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.25rem',
                borderRadius: '0.75rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                minWidth: '130px',
                justifyContent: 'center'
              }}
            >
              {isExporting ? (
                <>
                  <div className={styles.submitSpinner}></div>
                  Exporting...
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon style={{ width: '1rem', height: '1rem' }} />
                  Export Excel
                </>
              )}
            </button>

            <button
              onClick={handlePrint}
              className={styles.navButton}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.25rem',
                borderRadius: '0.75rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <PrinterIcon style={{ width: '1rem', height: '1rem' }} />
              Print Report
            </button>
          </div>
        </div>

        {/* Application Result */}
        {applicationResult && (
          <div className={`${styles.submissionResult} ${
            applicationResult.success ? styles.success : styles.error
          }`}>
            <div className={styles.submissionResultContent}>
              {applicationResult.success ? (
                <CheckCircleIcon className={`${styles.submissionResultIcon} ${styles.success}`} />
              ) : (
                <ExclamationTriangleIcon className={`${styles.submissionResultIcon} ${styles.error}`} />
              )}
              <div className={styles.submissionResultText}>
                <span className={`${styles.submissionResultMessage} ${
                  applicationResult.success ? styles.success : styles.error
                }`}>
                  {applicationResult.message}
                </span>
                {applicationResult.details && applicationResult.details.length > 0 && (
                  <div style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
                    {applicationResult.details.map((detail, index) => (
                      <div key={index} style={{ 
                        color: detail.status === 'success' ? '#059669' : '#dc2626',
                        marginBottom: '0.25rem',
                        fontSize: '0.8125rem'
                      }}>
                        • {detail.employeeName || detail.employeeId}: {detail.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Report Content */}
        <div ref={reportRef} style={{
          background: 'white',
          borderRadius: '1rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          border: '1px solid #e5e7eb'
        }}>
          {/* Report Header with Company Info */}
          <div className="print-header" style={{
            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
            color: 'white',
            padding: '3rem 2rem',
            textAlign: 'center'
          }}>
            {/* Phoenician Logo */}
            <img 
              src="/images/Phoenicianogo.png" 
              alt="Phoenician Logo"
              className="company-logo"
              style={{
                maxWidth: '220px',
                height: 'auto',
                marginBottom: '1.5rem',
                filter: 'brightness(0) invert(1)'
              }}
            />
            
            <h1 className="company-title" style={{
              margin: '0 0 0.75rem 0',
              fontSize: '2rem',
              fontWeight: '700',
              letterSpacing: '0.5px'
            }}>
              PHOENICIAN TECHNICAL SERVICES LLC
            </h1>
            
            <h2 className="report-title" style={{
              margin: '0 0 1.5rem 0',
              fontSize: '1.5rem',
              fontWeight: '600',
              opacity: 0.95
            }}>
              No Break Report
            </h2>
            
            <div className="company-info" style={{
              fontSize: '0.95rem',
              opacity: 0.9,
              lineHeight: '1.6'
            }}>
              <p style={{ margin: '0.5rem 0', fontWeight: '500' }}>
                Schon Business Park - 399 1 - Dubai Investments Park - Dubai
              </p>
              <p style={{ margin: '0.5rem 0' }}>
                <strong>Phone:</strong> 04 882 9484
              </p>
            </div>
          </div>

          {/* Project Information */}
          <div style={{ padding: '2.5rem' }}>
            <div className="info-section" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '2rem',
              marginBottom: '2.5rem',
              padding: '2rem',
              background: 'linear-gradient(135deg, #f0fdf4, #f7fee7)',
              borderRadius: '1rem',
              border: '2px solid #bbf7d0'
            }}>
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <BuildingOfficeIcon style={{ width: '1.5rem', height: '1.5rem', color: '#059669' }} />
                  <span style={{ fontWeight: '700', color: '#047857', fontSize: '1.125rem' }}>Project Details</span>
                </div>
                <div className="info-item">
                  <span className="info-label" style={{ fontWeight: '600', color: '#047857' }}>Project Name: </span>
                  <span className="info-value" style={{ color: '#059669' }}>{reportData.projectName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label" style={{ fontWeight: '600', color: '#047857' }}>Project Code: </span>
                  <span className="info-value" style={{ color: '#059669', fontFamily: 'monospace' }}>{reportData.projectCode}</span>
                </div>
              </div>

              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <CalendarDaysIcon style={{ width: '1.5rem', height: '1.5rem', color: '#059669' }} />
                  <span style={{ fontWeight: '700', color: '#047857', fontSize: '1.125rem' }}>Date & Time</span>
                </div>
                <div className="info-item">
                  <span className="info-label" style={{ fontWeight: '600', color: '#047857' }}>Date: </span>
                  <span className="info-value" style={{ color: '#059669' }}>{reportData.selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                  })}</span>
                </div>
                <div className="info-item">
                  <span className="info-label" style={{ fontWeight: '600', color: '#047857' }}>Applied At: </span>
                  <span className="info-value" style={{ color: '#059669' }}>{reportData.appliedAt.toLocaleString()}</span>
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <UserGroupIcon style={{ width: '1.5rem', height: '1.5rem', color: '#059669' }} />
                  <span style={{ fontWeight: '700', color: '#047857', fontSize: '1.125rem' }}>Application Details</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.75rem' }}>
                  <div className="info-item">
                    <span className="info-label" style={{ fontWeight: '600', color: '#047857' }}>Applied By: </span>
                    <span className="info-value" style={{ color: '#059669' }}>{reportData.appliedBy}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label" style={{ fontWeight: '600', color: '#047857' }}>Total Employees: </span>
                    <span className="info-value" style={{ color: '#059669', fontWeight: '600' }}>{reportData.employees.length}</span>
                  </div>
                </div>
                <div className="info-item" style={{ marginTop: '0.75rem' }}>
                  <span className="info-label" style={{ fontWeight: '600', color: '#047857' }}>Reason: </span>
                  <span className="info-value" style={{ color: '#059669' }}>{reportData.reason}</span>
                </div>
              </div>
            </div>

            {/* Employee Table */}
            <div style={{ marginBottom: '2.5rem' }}>
              <h3 style={{
                margin: '0 0 1.5rem 0',
                fontSize: '1.375rem',
                fontWeight: '700',
                color: '#322821',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                paddingBottom: '0.75rem',
                borderBottom: '2px solid #e5e7eb'
              }}>
                <UserGroupIcon style={{ width: '1.5rem', height: '1.5rem', color: '#c29e5d' }} />
                Employee List ({reportData.employees.length} employees)
              </h3>
              
              <div style={{ 
                overflowX: 'auto',
                border: '2px solid #e5e7eb',
                borderRadius: '1rem',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}>
                <table className="report-table" style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem'
                }}>
                  <thead style={{
                    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)'
                  }}>
                    <tr>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'center',
                        fontWeight: '700',
                        color: '#374151',
                        borderBottom: '2px solid #d1d5db',
                        fontSize: '0.8125rem'
                      }}>S.No</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '700',
                        color: '#374151',
                        borderBottom: '2px solid #d1d5db',
                        fontSize: '0.8125rem'
                      }}>Employee Name</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'center',
                        fontWeight: '700',
                        color: '#374151',
                        borderBottom: '2px solid #d1d5db',
                        fontSize: '0.8125rem'
                      }}>AUX No</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '700',
                        color: '#374151',
                        borderBottom: '2px solid #d1d5db',
                        fontSize: '0.8125rem'
                      }}>Designation</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '700',
                        color: '#374151',
                        borderBottom: '2px solid #d1d5db',
                        fontSize: '0.8125rem'
                      }}>Department</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'center',
                        fontWeight: '700',
                        color: '#374151',
                        borderBottom: '2px solid #d1d5db',
                        fontSize: '0.8125rem'
                      }}>Check In</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'center',
                        fontWeight: '700',
                        color: '#374151',
                        borderBottom: '2px solid #d1d5db',
                        fontSize: '0.8125rem'
                      }}>Check Out</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'center',
                        fontWeight: '700',
                        color: '#374151',
                        borderBottom: '2px solid #d1d5db',
                        fontSize: '0.8125rem'
                      }}>Attendance Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.employees.map((employee, index) => (
                      <tr key={employee.id} style={{
                        borderBottom: '1px solid #f3f4f6',
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                      }}>
                        <td style={{
                          padding: '0.875rem 0.75rem',
                          fontSize: '0.8125rem',
                          color: '#374151',
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          {index + 1}
                        </td>
                        <td style={{
                          padding: '0.875rem 0.75rem',
                          fontSize: '0.8125rem',
                          color: '#111827',
                          fontWeight: '600'
                        }}>
                          {employee.name}
                        </td>
                        <td style={{
                          padding: '0.875rem 0.75rem',
                          fontSize: '0.8125rem',
                          color: '#374151',
                          fontFamily: 'monospace',
                          textAlign: 'center',
                          fontWeight: '600'
                        }}>
                          {employee.pin || employee.employeeNumber || 'N/A'}
                        </td>
                        <td style={{
                          padding: '0.875rem 0.75rem',
                          fontSize: '0.8125rem',
                          color: '#374151'
                        }}>
                          {employee.designation}
                        </td>
                        <td style={{
                          padding: '0.875rem 0.75rem',
                          fontSize: '0.8125rem',
                          color: '#374151'
                        }}>
                          {employee.department}
                        </td>
                        <td style={{
                          padding: '0.875rem 0.75rem',
                          fontSize: '0.8125rem',
                          color: employee.checkInTime ? '#059669' : '#dc2626',
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          {employee.checkInTime || 'Not recorded'}
                        </td>
                        <td style={{
                          padding: '0.875rem 0.75rem',
                          fontSize: '0.8125rem',
                          color: employee.checkOutTime ? '#059669' : '#dc2626',
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          {employee.checkOutTime || 'Not recorded'}
                        </td>
                        <td style={{
                          padding: '0.875rem 0.75rem',
                          fontSize: '0.8125rem',
                          textAlign: 'center'
                        }}>
                          <span className="status-badge" style={{
                            padding: '0.375rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            background: employee.hasAttendanceToday ? '#d1fae5' : '#fee2e2',
                            color: employee.hasAttendanceToday ? '#065f46' : '#991b1b'
                          }}>
                            {employee.hasAttendanceToday ? (employee.attendanceStatus || 'Present') : 'No Attendance'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary Statistics */}
            <div className="stats-section" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2.5rem',
              padding: '2rem',
              background: 'linear-gradient(135deg, #fff7ed, #fed7aa)',
              borderRadius: '1rem',
              border: '2px solid #fdba74'
            }}>
              <div className="stat-item" style={{ textAlign: 'center' }}>
                <div className="stat-value" style={{ 
                  fontSize: '2rem', 
                  fontWeight: '800', 
                  color: '#ea580c',
                  marginBottom: '0.5rem'
                }}>
                  {reportData.employees.length}
                </div>
                <div className="stat-label" style={{ 
                  fontSize: '0.875rem', 
                  color: '#9a3412',
                  fontWeight: '600'
                }}>
                  Total Employees
                </div>
              </div>
              <div className="stat-item" style={{ textAlign: 'center' }}>
                <div className="stat-value" style={{ 
                  fontSize: '2rem', 
                  fontWeight: '800', 
                  color: '#ea580c',
                  marginBottom: '0.5rem'
                }}>
                  {reportData.employees.filter(emp => emp.hasAttendanceToday).length}
                </div>
                <div className="stat-label" style={{ 
                  fontSize: '0.875rem', 
                  color: '#9a3412',
                  fontWeight: '600'
                }}>
                  With Attendance
                </div>
              </div>
              <div className="stat-item" style={{ textAlign: 'center' }}>
                <div className="stat-value" style={{ 
                  fontSize: '2rem', 
                  fontWeight: '800', 
                  color: '#ea580c',
                  marginBottom: '0.5rem'
                }}>
                  {reportData.employees.filter(emp => !emp.hasAttendanceToday).length}
                </div>
                <div className="stat-label" style={{ 
                  fontSize: '0.875rem', 
                  color: '#9a3412',
                  fontWeight: '600'
                }}>
                  No Attendance
                </div>
              </div>
              <div className="stat-item" style={{ textAlign: 'center' }}>
                <div className="stat-value" style={{ 
                  fontSize: '2rem', 
                  fontWeight: '800', 
                  color: '#ea580c',
                  marginBottom: '0.5rem'
                }}>
                  {reportData.employees.length > 0 ? 
                    ((reportData.employees.filter(emp => emp.hasAttendanceToday).length / reportData.employees.length) * 100).toFixed(1) : 
                    '0.0'
                  }%
                </div>
                <div className="stat-label" style={{ 
                  fontSize: '0.875rem', 
                  color: '#9a3412',
                  fontWeight: '600'
                }}>
                  Attendance Rate
                </div>
              </div>
            </div>

            {/* Disclaimer/Note Section */}
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
              border: '2px solid #f59e0b',
              borderRadius: '1rem',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem'
              }}>
                <ExclamationTriangleIcon style={{
                  width: '1.5rem',
                  height: '1.5rem',
                  color: '#d97706',
                  flexShrink: 0,
                  marginTop: '0.125rem'
                }} />
                <div>
                  <h4 style={{
                    margin: '0 0 0.75rem 0',
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    color: '#92400e'
                  }}>
                    Important Notice
                  </h4>
                  <p style={{
                    margin: '0',
                    fontSize: '0.875rem',
                    color: '#d97706',
                    lineHeight: '1.6'
                  }}>
                    This report certifies that the listed employees worked without taking mandatory breaks during their shift 
                    on {reportData.selectedDate.toLocaleDateString()} for the project "{reportData.projectName}". 
                    The reason specified was: <strong>"{reportData.reason}"</strong>. 
                    This document serves as official record for payroll and compliance purposes.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer with Landscape Logo */}
            <div className="footer-section" style={{
              textAlign: 'center',
              marginTop: '3rem',
              paddingTop: '2rem',
              borderTop: '3px solid #e5e7eb'
            }}>
              <img 
                src="/images/ptslogo.png" 
                alt="PTS Landscape Logo"
                className="footer-logo"
                style={{
                  maxWidth: '350px',
                  height: 'auto',
                  opacity: 0.8,
                  marginBottom: '1rem'
                }}
              />
              <div className="generated-info" style={{
                fontSize: '0.8125rem',
                color: '#6b7280',
                fontStyle: 'italic',
                lineHeight: '1.5'
              }}>
                <div>Report generated on {new Date().toLocaleString()}</div>
                <div>© 2025 Phoenician Technical Services LLC. All rights reserved.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Apply No-Break Button */}
        {!applicationResult?.success && (
          <div style={{
            marginTop: '2rem',
            padding: '2rem',
            background: 'white',
            borderRadius: '1rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: '2px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <div style={{
              marginBottom: '1.5rem'
            }}>
              <h3 style={{
                margin: '0 0 0.75rem 0',
                fontSize: '1.375rem',
                fontWeight: '700',
                color: '#322821'
              }}>
                Ready to Apply No-Break Status?
              </h3>
              <div style={{
                fontSize: '0.9375rem',
                color: '#6b7280',
                lineHeight: '1.6',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                This will officially mark all <strong>{reportData.employees.length}</strong> selected employees as having worked without breaks 
                for the project <strong>"{reportData.projectName}"</strong> on <strong>{reportData.selectedDate.toLocaleDateString()}</strong>.
                <br />
                <span style={{ fontSize: '0.875rem', fontStyle: 'italic', color: '#9ca3af' }}>
                  Only employees with attendance records will be processed.
                </span>
              </div>
            </div>
            
            {/* Stats before applying */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '2rem',
              marginBottom: '2rem',
              flexWrap: 'wrap'
            }}>
              <div style={{
                background: '#f0fdf4',
                padding: '1rem 1.5rem',
                borderRadius: '0.75rem',
                border: '1px solid #bbf7d0',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#047857' }}>
                  {reportData.employees.filter(emp => emp.hasAttendanceToday).length}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#059669' }}>Will be processed</div>
              </div>
              <div style={{
                background: '#fef2f2',
                padding: '1rem 1.5rem',
                borderRadius: '0.75rem',
                border: '1px solid #fecaca',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#dc2626' }}>
                  {reportData.employees.filter(emp => !emp.hasAttendanceToday).length}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#dc2626' }}>Will be skipped</div>
              </div>
            </div>
            
            <button
              onClick={handleApplyNoBreak}
              disabled={isApplying}
              className={styles.navButton}
              style={{
                background: isApplying 
                  ? '#9ca3af' 
                  : 'linear-gradient(135deg, #dc2626, #ef4444)',
                color: 'white',
                border: 'none',
                padding: '1.25rem 2.5rem',
                borderRadius: '0.75rem',
                fontSize: '1.125rem',
                fontWeight: '700',
                cursor: isApplying ? 'not-allowed' : 'pointer',
                minWidth: '250px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                margin: '0 auto',
                transition: 'all 0.2s ease',
                boxShadow: isApplying ? 'none' : '0 4px 12px rgba(220, 38, 38, 0.3)'
              }}
            >
              {isApplying ? (
                <>
                  <div className={styles.submitSpinner}></div>
                  Applying No-Break Status...
                </>
              ) : (
                <>
                  <CheckCircleIcon style={{ width: '1.5rem', height: '1.5rem' }} />
                  Apply No-Break Status Now
                </>
              )}
            </button>

            {/* Warning text */}
            <div style={{
              marginTop: '1.5rem',
              fontSize: '0.8125rem',
              color: '#dc2626',
              fontWeight: '500',
              fontStyle: 'italic'
            }}>
              ⚠️ This action cannot be undone. Please verify all information before proceeding.
            </div>
          </div>
        )}

        {/* Success message after application */}
        {applicationResult?.success && (
          <div style={{
            marginTop: '2rem',
            padding: '2rem',
            background: 'linear-gradient(135deg, #f0fdf4, #f7fee7)',
            border: '2px solid #bbf7d0',
            borderRadius: '1rem',
            textAlign: 'center'
          }}>
            <CheckCircleIcon style={{
              width: '3rem',
              height: '3rem',
              color: '#059669',
              margin: '0 auto 1rem auto'
            }} />
            <h3 style={{
              margin: '0 0 1rem 0',
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#047857'
            }}>
              No-Break Status Applied Successfully!
            </h3>
            <p style={{
              margin: '0',
              fontSize: '1rem',
              color: '#059669',
              lineHeight: '1.6'
            }}>
              The no-break status has been officially recorded for all eligible employees. 
              You can now export this report or return to the main dashboard.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NoBreakReportPage;